/** API client with JWT Bearer from oidc-client-ts (SPA_API_CLIENT). */

import { getUserManager } from "@/hooks/useAuth";
import { getConfig } from "@/auth/config";

async function getToken(): Promise<string | null> {
  const um = getUserManager();
  if (!um) return null;
  try {
    const user = await um.getUser();
    return user?.id_token ?? null;
  } catch {
    return null;
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const config = getConfig();
  const base = config?.apiEndpoint ?? "";
  const token = await getToken();

  if (!token) {
    // Not authenticated yet — don't send unauthenticated requests
    throw new Error("Not authenticated");
  }

  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (res.status === 401) {
    getUserManager()?.signinRedirect();
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `${res.status} ${res.statusText}`);
  }

  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
